import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { useEffect, useState } from "react";
import { Compass, ChevronDown, ListOrdered, Check } from "lucide-react";
import { QuickReorder } from "./QuickReorder";
import { PeopleSheet } from "./PeopleSheet";
import { CountryPicker } from "./CountryPicker";
import { WishItem } from "./WishItem";
import { usePlans } from "@/hooks/usePlans";
import { PendingInvites } from "@/components/plans/PendingInvites";

export const WantToGo = ({ wishlist, names, b, onFocus, beenTo = [] }) => {
  const been = new Set(beenTo.map((v) => v.country));
  const P = usePlans(!b.readOnly);
  const [all, setAll] = useState(false);
  const [quick, setQuick] = useState(false);
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 639px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const on = (e) => setMobile(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const limited = b.readOnly || mobile;
  const shown = limited && !all ? wishlist.slice(0, 10) : wishlist;
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const move = (from, to) => {
    if (to < 0 || to >= wishlist.length) return;
    b.reorder(arrayMove(wishlist, from, to));
  };
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    move(wishlist.findIndex((w) => w.id === active.id), wishlist.findIndex((w) => w.id === over.id));
  };

  return (
    <div data-testid="want-to-go-section">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-400">{b.readOnly ? "Next adventures, in order." : quick ? "Drag rows or use the arrows. Saves as you go." : "Drag or use arrows to set your order."}</p>
        {!b.readOnly && <span className="ml-auto mr-2"><PeopleSheet wishlist={wishlist} b={b} /></span>}
        {!b.readOnly && wishlist.length > 1 && (
          <button onClick={() => setQuick(!quick)} data-testid="quick-reorder-toggle"
            className={`mr-2 flex items-center gap-1.5 text-xs rounded-full border px-3 py-1.5 transition-colors ${quick ? "bg-amber-400 border-amber-400 text-[#1a1203]" : "border-white/15 text-slate-300 hover:border-amber-400/50 hover:text-amber-200"}`}>
            {quick ? <><Check size={13} /> Done</> : <><ListOrdered size={13} /> Quick reorder</>}
          </button>
        )}
        {!b.readOnly && !quick && <CountryPicker names={names} exclude={wishlist.map((w) => w.country)} onPick={b.addWish} label="Add" testId="add-wish-country-button"
          className="bg-amber-500 text-[#1a1203] hover:bg-amber-400" />}
      </div>
      {!b.readOnly && <PendingInvites P={P} />}
      {wishlist.length === 0 && (
        <div className="text-center py-10 text-slate-500 text-sm" data-testid="wish-empty"><Compass className="mx-auto mb-2 opacity-50" /> Where to next? Add your first dream destination.</div>
      )}
      {quick && !b.readOnly ? <QuickReorder wishlist={wishlist} move={move} been={been} /> : (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={wishlist.map((w) => w.id)} strategy={verticalListSortingStrategy}>
          <ol className="space-y-2">
            {shown.map((w, i) => (
              <WishItem key={w.id} w={w} i={i} last={i === wishlist.length - 1} b={b} onMove={move} onFocus={onFocus} P={P} cityFirst={been.has(w.country) && (w.cities || []).length > 0} />
            ))}
          </ol>
        </SortableContext>
      </DndContext>
      )}
      {limited && !quick && wishlist.length > 10 && (
        <button onClick={() => setAll(!all)} data-testid="wish-see-more-button"
          className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-amber-400/30 text-amber-300 text-sm hover:bg-amber-400/10 transition-colors">
          {all ? "Show top 10" : `See more · ${wishlist.length - 10} more`}
          <ChevronDown size={15} className={`transition-transform duration-200 ${all ? "rotate-180" : ""}`} />
        </button>
      )}
    </div>
  );
};
